import React from 'react';
import { OAuth2 } from '@backstage/core-app-api';
import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  BackstageIdentityApi,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  identityApiRef,
  oauthRequestApiRef,
  OpenIdConnectApi,
  OAuthApi,
  ProfileInfoApi,
  SessionApi,
  storageApiRef,
} from '@backstage/core-plugin-api';
import {
  apiDocsConfigRef,
  defaultDefinitionWidgets,
  OpenApiDefinitionWidget,
} from '@backstage/plugin-api-docs';
import { ApiEntity } from '@backstage/catalog-model';
import { visitsApiRef, VisitsStorageApi } from '@backstage/plugin-home';
import { collapseEntityTabs } from './visits';
import LockOpenIcon from '@material-ui/icons/LockOpen';

export const oidcAuthApiRef = createApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
>({
  id: 'internal.auth.oidc',
});

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: apiDocsConfigRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => {
      const widgets = defaultDefinitionWidgets();
      return {
        getApiDefinitionWidget: (apiEntity: ApiEntity) => {
          if (apiEntity.spec.type === 'openapi') {
            return {
              type: 'openapi',
              title: 'OpenAPI',
              component: (definition: string) =>
                React.createElement(OpenApiDefinitionWidget, {
                  definition,
                  requestInterceptor: (req: any) => {
                    const mappings =
                      configApi.getOptionalConfigArray(
                        'apiDocs.swaggerProxyMappings',
                      ) ?? [];
                    const backendBaseUrl =
                      configApi.getString('backend.baseUrl');
                    for (const mapping of mappings) {
                      const from = mapping.getString('from');
                      const to = mapping.getString('to');
                      if (req.url?.startsWith(from)) {
                        req.url = req.url.replace(
                          from,
                          `${backendBaseUrl}/api/proxy/${to}`,
                        );
                        break;
                      }
                    }
                    return req;
                  },
                }),
            };
          }
          return widgets.find(w => w.type === apiEntity.spec.type);
        },
      };
    },
  }),

  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: oidcAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        discoveryApi,
        oauthRequestApi,
        configApi,
        provider: {
          id: 'oidc',
          title: 'revenexx ID',
          icon: LockOpenIcon,
        },
        defaultScopes: [
          'openid',
          'profile',
          'email',
          'offline_access',
          // Ask ZITADEL to include user metadata (e.g. github_username) in the
          // userinfo response so the sign-in resolver can map the user to a
          // GitHub login. Returned under the claim
          // `urn:zitadel:iam:user:metadata` as a base64-encoded map.
          'urn:zitadel:iam:user:metadata',
        ],
      }),
  }),
  createApiFactory({
    api: visitsApiRef,
    deps: {
      storageApi: storageApiRef,
      identityApi: identityApiRef,
    },
    factory: ({ storageApi, identityApi }) =>
      VisitsStorageApi.create({
        storageApi,
        identityApi,
        // One visit per entity, whichever of its tabs was open (DX-349).
        transformPathname: collapseEntityTabs,
      }),
  }),
];
